import { useContext, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { IOrg } from '@unfoldrtech/portal-mic'
import { AppContext } from '../models/contexts'
import { CFeatureFlagContext } from '../models/classes'
import { useFeatureFlagClient } from './useFeatureFlagClient'

function parseOrgIdsFromPath(pathname: string): {
  advertiserId?: number
  retailerId?: number
} {
  const advertiserMatch = pathname.match(/\/advertiser\/(\d+)/)
  const retailerMatch = pathname.match(/\/retailer\/(\d+)/)
  const advertiserId = advertiserMatch ? Number(advertiserMatch[1]) : undefined
  const retailerId = retailerMatch ? Number(retailerMatch[1]) : undefined
  return {
    advertiserId:
      typeof advertiserId === 'number' && !Number.isNaN(advertiserId)
        ? advertiserId
        : undefined,
    retailerId:
      typeof retailerId === 'number' && !Number.isNaN(retailerId)
        ? retailerId
        : undefined,
  }
}

export function useSyncFeatureFlagContext(): void {
  const [appContext] = useContext(AppContext)
  const ldClient = useFeatureFlagClient()
  const location = useLocation()

  const { advertiserId, retailerId } = useMemo(
    () => parseOrgIdsFromPath(location.pathname),
    [location.pathname]
  )

  const userProfile = appContext.userProfile
  const userClaims = appContext.userClaims

  const isMountedRef = useRef(true)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const identityKey = useMemo(() => {
    if (!userProfile?.id || !userClaims?.userRole) return null
    if (!advertiserId && !retailerId) return null
    return `u:${userProfile.id}|role:${userClaims.userRole}|a:${
      advertiserId ?? '-'
    }|r:${retailerId ?? '-'}`
  }, [userProfile?.id, userClaims?.userRole, advertiserId, retailerId])

  useEffect(() => {
    if (!ldClient || !identityKey) return
    if (!userProfile || !userClaims) return
    if (lastKeyRef.current === identityKey) return

    lastKeyRef.current = identityKey

    const ffContext = new CFeatureFlagContext({
      user: {
        id: userProfile.id,
        email: userProfile.email,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        locale: userProfile.locale,
        role: userClaims.userRole,
      },
      advertiser: advertiserId ? ({ id: advertiserId } as IOrg) : ({} as IOrg),
      retailer: retailerId ? ({ id: retailerId } as IOrg) : ({} as IOrg),
    })

    ;(async () => {
      try {
        await ldClient.identify(ffContext)
        if (!isMountedRef.current) return
        await ldClient.flush()
      } catch {
        if (lastKeyRef.current === identityKey) {
          lastKeyRef.current = null
        }
      }
    })()
  }, [ldClient, identityKey])
}
